import { useFeatureAccess } from '../contexts/FeatureAccessContext';
import { useAuth } from '../contexts/AuthContext';
import { useState, useEffect } from 'react';
import { Lock, Clock, Loader2, CheckCircle, Mail } from 'lucide-react';
import { toast } from 'sonner';

export function FeatureGate({ featureKey, children, showUnlessDisabled = false }) {
  const { canAccess, loaded } = useFeatureAccess();
  const { user, api } = useAuth();

  if (!loaded) return null; // wait — never flash gated content

  const access = canAccess(featureKey);

  if (!access) return children; // No feature info, allow access
  if (access.accessible) return children;

  // showUnlessDisabled (e.g. /plans, the upgrade destination): only an explicit
  // admin disable (global off / disabled for this user) blocks the page —
  // plan-gating and "coming soon" stay reachable so an under-tier user can
  // still open the page that lets them upgrade (no catch-22). Mirrors the
  // showUnlessDisabled nav-item behaviour in Layout.js.
  if (showUnlessDisabled && access.reason !== 'feature_disabled' && access.reason !== 'disabled_for_user') {
    return children;
  }

  if (access.coming_soon) {
    return <ComingSoonPage featureKey={featureKey} user={user} api={api} />;
  }

  // access.accessible is false and it's not "coming soon": either plan-gated
  // (a higher plan unlocks it → show the upgrade CTA) or simply disabled by an
  // admin (global off / disabled for this user → no upgrade path, just "not
  // available"). The disabled case has no required_plan, so don't show a
  // bogus "requires the [blank] plan" message.
  const isPlanGated = !!access.required_plan;
  return (
    <div className="flex items-center justify-center min-h-[60vh]" data-testid={`feature-gate-${featureKey}`}>
      <div className="text-center max-w-md mx-auto p-8">
        <div className="w-16 h-16 rounded-2xl mx-auto mb-6 flex items-center justify-center"
          style={{ background: isPlanGated ? 'var(--warning-light)' : 'var(--bg-main)' }}>
          <Lock size={28} style={{ color: isPlanGated ? 'var(--warning)' : 'var(--text-light)' }} />
        </div>
        <h2 className="text-xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
          {isPlanGated ? 'Upgrade Required' : 'Not Available'}
        </h2>
        {isPlanGated ? (
          <>
            <p className="text-sm mb-1" style={{ color: 'var(--text-muted)' }}>
              This feature requires the <strong className="capitalize">{access.required_plan}</strong> plan or higher.
            </p>
            <p className="text-xs" style={{ color: 'var(--text-light)' }}>
              Go to Plans to upgrade your subscription.
            </p>
          </>
        ) : (
          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
            This feature isn't available right now.
          </p>
        )}
      </div>
    </div>
  );
}

function ComingSoonPage({ featureKey, user, api }) {
  const [email, setEmail] = useState(user?.email || '');
  const [submitting, setSubmitting] = useState(false);
  const [joined, setJoined] = useState(false);
  const [waitlistEnabled, setWaitlistEnabled] = useState(false);

  useEffect(() => {
    // Check if waitlist is enabled for this feature
    (api || { get: () => Promise.reject() }).get('/waitlist/settings')
      .then(res => {
        const s = res.data?.find(w => w.slug === featureKey);
        if (s?.waitlist_enabled) setWaitlistEnabled(true);
      })
      .catch(() => {});
  }, [featureKey, api]);

  const handleJoin = async () => {
    if (!email || !email.includes('@')) {
      toast.error('Please enter a valid email');
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post('/waitlist', {
        email,
        feature_slug: featureKey,
        source_url: window.location.pathname,
        user_id: user?.id,
      });
      if (res.data.status === 'already_registered') {
        toast.info(res.data.message);
      } else {
        toast.success(res.data.message);
      }
      setJoined(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to join waitlist');
    }
    setSubmitting(false);
  };

  return (
    <div className="flex items-center justify-center min-h-[60vh]" data-testid={`coming-soon-${featureKey}`}>
      <div className="text-center max-w-lg mx-auto p-8">
        <div className="w-16 h-16 rounded-2xl mx-auto mb-6 flex items-center justify-center" style={{ background: 'var(--brand-primary-light)' }}>
          <Clock size={28} style={{ color: 'var(--brand-primary)' }} />
        </div>
        <h2 className="text-xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Coming Soon</h2>
        <p className="text-sm mb-6" style={{ color: 'var(--text-muted)' }}>
          We're working hard to bring this feature to you. Stay tuned!
        </p>

        {waitlistEnabled && !joined && (
          <div className="mt-4 p-5 rounded-2xl max-w-lg mx-auto" style={{ background: 'var(--bg-main)', border: '1px solid var(--border-color)' }}
            data-testid={`waitlist-form-${featureKey}`}>
            <p className="text-sm font-semibold mb-3" style={{ color: 'var(--text-main)' }}>
              Get notified when it's ready
            </p>
            <div className="flex gap-2">
              <div className="relative flex-1 min-w-0">
                <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-light)' }} />
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="Your email"
                  className="nsofar-input text-sm h-10 w-full"
                  style={{ paddingLeft: '2.25rem' }}
                  data-testid={`waitlist-email-${featureKey}`}
                />
              </div>
              <button
                onClick={handleJoin}
                disabled={submitting}
                className="nsofar-btn nsofar-btn-primary text-sm h-10 px-5 flex-shrink-0 whitespace-nowrap"
                data-testid={`waitlist-submit-${featureKey}`}
              >
                {submitting ? <Loader2 size={14} className="animate-spin" /> : 'Join Waitlist'}
              </button>
            </div>
          </div>
        )}

        {joined && (
          <div className="mt-4 p-4 rounded-2xl flex items-center gap-3 justify-center"
            style={{ background: 'var(--success-light)' }}
            data-testid={`waitlist-success-${featureKey}`}>
            <CheckCircle size={18} style={{ color: 'var(--success)' }} />
            <span className="text-sm font-medium" style={{ color: 'var(--success)' }}>You're on the list!</span>
          </div>
        )}
      </div>
    </div>
  );
}
